/**
 * Delete Account Page
 * SaccoTeck — standalone route for Google Play's required account-deletion
 * request page. Styling matches LoginPage.jsx / PrivacyPolicyPage.jsx.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

const NAVY = '#0D1B2A';
const GOLD = '#C9A84C';
const CREAM = '#F8F5F0';
const MUTED = '#6B7280';
const BORDER = '#e2d9cc';

const SUPPORT_EMAIL = 'jeprosservices@gmail.com';

const stepStyle = {
  display: 'flex', gap: 16, marginBottom: 24, alignItems: 'flex-start',
};
const stepNumStyle = {
  width: 32, height: 32, borderRadius: 16, background: NAVY, color: GOLD,
  fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 15,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
const stepTitleStyle = { fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 4 };
const stepTextStyle = { fontSize: 14, color: '#374151', lineHeight: 1.7, margin: 0 };

const DeleteAccountPage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: CREAM, minHeight: '100vh' }}>
      {/* ── HEADER ── */}
      <div style={{
        background: NAVY, padding: '20px 6%', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: GOLD }}>
            SaccoTeck
          </span>
          <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 1 }}>BY JEPROS TECK</div>
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'transparent', color: '#fff', border: `1px solid ${GOLD}`,
            padding: '10px 20px', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ← Back to Home
        </button>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 24px 90px' }}>
        <p style={{ fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: GOLD, marginBottom: 12 }}>
          Account &amp; Data
        </p>
        <h1 style={{
          fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4vw, 42px)',
          color: NAVY, marginBottom: 16, lineHeight: 1.15,
        }}>
          Request Account Deletion
        </h1>
        <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 40 }}>
          If you would like your SaccoTeck member account and associated personal data deleted,
          follow the steps below. This applies to your login credentials, profile information, and
          app access — please read the retention note at the bottom regarding SACCO financial
          records.
        </p>

        <div style={{
          background: '#fff', borderRadius: 12, padding: '40px 36px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 32,
        }}>
          <div style={stepStyle}>
            <div style={stepNumStyle}>1</div>
            <div>
              <p style={stepTitleStyle}>Send a deletion request</p>
              <p style={stepTextStyle}>
                Email <strong>{SUPPORT_EMAIL}</strong> from the address registered to your SaccoTeck
                account, with the subject line <strong>"Account Deletion Request"</strong>. Include
                your full name and Member ID so we can locate your account.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumStyle}>2</div>
            <div>
              <p style={stepTitleStyle}>Identity verification</p>
              <p style={stepTextStyle}>
                Our team will verify your identity as the account holder, typically by confirming
                details already on file (Member ID, phone number, or ID number), before processing
                the request.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumStyle}>3</div>
            <div>
              <p style={stepTitleStyle}>Confirmation and processing</p>
              <p style={stepTextStyle}>
                We will confirm receipt of your request within 5 business days and complete the
                deletion of your login account and app-related personal data within 30 days.
              </p>
            </div>
          </div>

          <div style={{ ...stepStyle, marginBottom: 0 }}>
            <div style={stepNumStyle}>4</div>
            <div>
              <p style={stepTitleStyle}>Deletion confirmed</p>
              <p style={stepTextStyle}>
                You will receive an email confirming your account and associated login data have
                been deleted.
              </p>
            </div>
          </div>
        </div>

        <div style={{
          background: '#fff8e8', border: `1px solid ${GOLD}`, borderRadius: 10,
          padding: '20px 24px', marginBottom: 32,
        }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 6 }}>
            A note on SACCO financial records
          </p>
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, margin: 0 }}>
            As a registered SACCO, we are required by law to retain certain financial and membership
            records (such as share contributions, loan history, and transaction records) for
            regulatory and audit purposes, even after your login account is deleted. Deleting your
            app account removes your login access and app-related personal data, but does not
            erase statutory financial records the SACCO is legally obligated to keep. If you wish to
            fully exit your SACCO membership, please contact your SACCO administrator directly.
          </p>
        </div>

        <div style={{
          background: NAVY, borderRadius: 10, padding: '24px 28px',
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 28 }}>📧</span>
          <div>
            <p style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              Send your request to
            </p>
            <p style={{ fontSize: 16, fontWeight: 700, color: GOLD, margin: 0 }}>{SUPPORT_EMAIL}</p>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: 12, color: MUTED, marginTop: 40 }}>
          © {new Date().getFullYear()} Jepros Teck · Cable Systems Interprise
        </p>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
